import { CloudDownload } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { concat, orderBy, sumBy } from 'lodash';
import { makeStyles } from 'tss-react/mui';
import { addSeconds } from 'date-fns';

import Table from 'components/Table';
import DownloadPdfExitStatementButton from './DownloadPdfExitStatementButton';
import { dateToLabel, numberToDisplayString } from 'further-ui/utils';
import { useAllocations } from 'hooks/data/allocation/useAllocations';
import { useExportToCustodian } from 'hooks/data/allocation/useUploadAllocations';
import useCompanyShareholdings from 'hooks/data/company/useCompaniesShareholdings';
import { SortOrder } from 'further-types/utils';

enum AllocationRecordType {
  Allocation = 'allocation',
  Exit = 'exit',
  ExitConsideration = 'exitConsideration',
}

interface TableRow {
  _id: string;
  type: AllocationRecordType;
  date: Date | string;
  companyId: string | { _id: string; tradingName: string };
  sharesAcquired?: number;
  sharesSold?: number;
  selectedInvestors?: Array<{ _id: string; fullName: string }>;
  investorCount: number;
}

const useStyles = makeStyles()(() => ({
  summaryRow: {
    backgroundColor: '#FBFBFA',
    marginTop: 4,
  },
  summaryValue: {
    color: '#282525',
    fontSize: 14,
    fontWeight: 'bold',
    padding: '10px 10px 10px 6px',
  },
}));

type Props = {
  companyId?: string;
};

const AllocationRecords: React.FC<Props> = ({ companyId }) => {
  const { classes } = useStyles();
  const exportToCustodian = useExportToCustodian();

  const allocations = useAllocations({
    params: {
      companyIds: companyId ? [companyId] : [],
      keyName: 'date',
      type: SortOrder.Desc,
      page: 1,
      perPage: 1000,
    },
  });

  const shareHoldings = useCompanyShareholdings({
    companyId: companyId ?? '',
    page: '1',
    pageSize: '1000',
    order: SortOrder.Desc,
    orderBy: 'date',
  });
  const getAllocationRows = (): Array<TableRow> => {
    // Filter out allocations with exitId - these should be handled by getExitConsiderationRows()
    const rows =
      allocations.data?.uploadShareHoldings
        .filter((allocation) => !allocation.exitId)
        .map((allocation) => ({
          _id: allocation._id,
          type: AllocationRecordType.Allocation,
          date: new Date(allocation.date),
          companyId: allocation.companyId,
          sharesAcquired: allocation.totalNumberOfShare,
          investorCount: allocation.numberOfInvestors,
        })) ?? [];

    return rows;
  };

  const getExitRows = (): Array<TableRow> => {
    const exitsById = new Map<
      string,
      {
        _id: string;
        companyId: string;
        date: Date | string;
        sharesSold: number;
        investors: Set<string>;
        investorNames: Map<string, string>;
      }
    >();

    for (const sh of shareHoldings.data?.result ?? []) {
      for (const sm of sh.shareMovements) {
        if (!exitsById.has(sm.exitId)) {
          exitsById.set(sm.exitId, {
            _id: sm.exitId,
            companyId: sm.companyId,
            date: sh.shareMovements[0].date,
            sharesSold: 0,
            investors: new Set<string>(),
            investorNames: new Map<string, string>(),
          });
        }

        const exit = exitsById.get(sm.exitId);
        if (!exit) continue;
        exit.sharesSold += sm.noOfShares;
        exit.investors.add(sm.investorId);
        exit.investorNames.set(sm.investorId, sh.fullName);
      }
    }

    return Array.from(exitsById.values()).map((x) => ({
      _id: x._id,
      type: AllocationRecordType.Exit,
      companyId: x.companyId,
      date: x.date
        ? addSeconds(typeof x.date === 'string' ? new Date(x.date) : x.date, 1)
        : x.date,
      sharesSold: x.sharesSold,
      selectedInvestors: Array.from(x.investorNames.entries()).map(
        ([id, fullName]) => ({ _id: id, fullName }),
      ),
      investorCount: x.investors.size,
    }));
  };

  const getExitConsiderationRows = (): Array<TableRow> => {
    // Get all regular allocation IDs (these exclude exit-related ones)
    // Exclude allocations with exitId so they can be processed here from shareholdings data
    const regularAllocationIds = new Set(
      allocations.data?.uploadShareHoldings
        .filter((a) => !a.exitId)
        .map((a) => a._id) ?? [],
    );

    // Group shareholdings by shareAllocationUploadId that are NOT in regular allocations
    // These are shareholdings that came from exits (ShareAllocationUploads with exitId/exitAmendmentId)
    const transactionsByAllocationId = new Map<
      string,
      {
        _id: string;
        date: Date | string;
        sharesAcquired: number;
        investors: Set<string>;
        investorNames: Map<string, string>;
      }
    >();

    for (const sh of shareHoldings.data?.result ?? []) {
      const shareAllocationUploadId = sh.shareAllocationUploadId;
      if (!shareAllocationUploadId) continue;

      // If this shareAllocationUploadId is NOT in regular allocations, it's from an exit
      if (!regularAllocationIds.has(shareAllocationUploadId)) {
        if (!transactionsByAllocationId.has(shareAllocationUploadId)) {
          transactionsByAllocationId.set(shareAllocationUploadId, {
            _id: shareAllocationUploadId,
            date: sh.date ?? new Date(),
            sharesAcquired: 0,
            investors: new Set<string>(),
            investorNames: new Map<string, string>(),
          });
        }

        const transaction = transactionsByAllocationId.get(
          shareAllocationUploadId,
        );
        if (!transaction) continue;

        // Tally is the number of shares in the shareholding
        transaction.sharesAcquired += sh.sharesCurrentlyHeld ?? 0;
        transaction.investors.add(sh.investmentId);
        transaction.investorNames.set(sh.investmentId, sh.fullName);
      }
    }

    const rows = Array.from(transactionsByAllocationId.values()).map((x) => ({
      _id: x._id,
      type: AllocationRecordType.ExitConsideration,
      companyId: companyId ?? '',
      date: x.date
        ? addSeconds(typeof x.date === 'string' ? new Date(x.date) : x.date, 2)
        : x.date,
      sharesAcquired: x.sharesAcquired,
      selectedInvestors: Array.from(x.investorNames.entries()).map(
        ([id, fullName]) => ({ _id: id, fullName }),
      ),
      investorCount: x.investors.size,
    }));

    return rows;
  };

  const allocationRows = getAllocationRows();
  const exitRows = getExitRows();
  const exitConsiderationRows = getExitConsiderationRows();

  const rows = orderBy(
    concat<TableRow>(allocationRows, exitRows, exitConsiderationRows),
    'date',
    'desc',
  );

  const handleAllocationDownload = (
    id: string,
    companyId: string | { _id: string; tradingName: string },
    allocationDate: Date | string,
  ) => {
    const companyTradingName =
      typeof companyId === 'object' ? companyId.tradingName ?? '' : '';
    exportToCustodian.mutate({
      shareAllocationUploadId: id,
      companyTradingName,
      allocationDate,
    });
  };

  const columns = [
    {
      label: 'Date',
      key: 'date',
      sort: false,
      render: (row: TableRow) => dateToLabel(row.date),
    },
    {
      label: 'Transaction',
      key: 'transaction',
      sort: false,
      render: (row: TableRow) => {
        if (row.type === AllocationRecordType.Allocation) return 'Allocation';
        if (row.type === AllocationRecordType.ExitConsideration)
          return 'Exit consideration';
        return 'Exit';
      },
    },
    {
      label: 'Shares acquired',
      key: 'sharesAcquired',
      sort: false,
      render: (row: TableRow) =>
        row.sharesAcquired ? numberToDisplayString(row.sharesAcquired) : '-',
    },
    {
      label: 'Shares sold',
      key: 'sharesSold',
      sort: false,
      render: (row: TableRow) =>
        row.sharesSold ? numberToDisplayString(row.sharesSold) : '-',
    },
    {
      label: 'Investors',
      key: 'investorCount',
      sort: false,
      render: (row: TableRow) => numberToDisplayString(row.investorCount),
    },
    {
      label: 'Actions',
      key: 'actions',
      render: (row: TableRow) => {
        if (row.type === AllocationRecordType.Allocation) {
          return (
            <IconButton
              title="Download"
              onClick={() =>
                handleAllocationDownload(row._id, row.companyId, row.date)
              }
              size="large"
            >
              <CloudDownload color="primary" />
            </IconButton>
          );
        }

        const isExitConsideration =
          row.type === AllocationRecordType.ExitConsideration;
        const exitId = isExitConsideration ? undefined : row._id;

        return (
          <DownloadPdfExitStatementButton
            exitId={exitId}
            selectedInvestors={row.selectedInvestors ?? []}
          />
        );
      },
    },
  ];

  return (
    <Table
      columns={columns}
      tablebody={rows}
      loading={allocations?.isFetching}
      fixedColumns={true}
      TableFooter={
        <tfoot>
          <tr className={classes.summaryRow}>
            <td />
            <td />
            <td className={classes.summaryValue}>
              {numberToDisplayString(sumBy(rows, 'sharesAcquired'))}
            </td>
            <td className={classes.summaryValue}>
              {numberToDisplayString(sumBy(rows, 'sharesSold'))}
            </td>
            <td />
            <td />
          </tr>
        </tfoot>
      }
    />
  );
};

export default AllocationRecords;
